/**
 * Un materiale del kit dei partner (03/11/2026): che cos'è, dove porta, e i suoi quattro messaggi (WhatsApp breve e
 * lungo, oggetto e testo dell'email). I problemi li dice la stessa regola del server (`problemiDelMateriale`), prima di
 * salvare; sotto i messaggi l'anteprima del WhatsApp breve com'è per un contatto vero, col link d'esempio.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { indirizzoInvito, INDIRIZZO_SITO } from '@shared/partner/codiceInvito';
import {
  TIPI_MATERIALE, componiMessaggio, problemiDelMateriale, type MaterialeDoc, type MessaggiDelMateriale, type TipoMateriale,
} from '@shared/partner/materiali';
import { salvaMateriale, type ConId } from '../../services/partnerConsoleService';

interface Props {
  aperto: boolean;
  /** Il materiale da correggere; assente = nuovo. */
  materiale: ConId<MaterialeDoc> | null;
  /** Il prossimo numero d'ordine, per un materiale nuovo. */
  ordineNuovo: number;
  onChiudi: () => void;
  onSalvato: () => void;
  onErrore: (messaggio: string) => void;
}

const MESSAGGI_VUOTI: MessaggiDelMateriale = { whatsapp_breve: '', whatsapp_lungo: '', email_oggetto: '', email_testo: '' };
const CAMPI: ReadonlyArray<{ campo: keyof MessaggiDelMateriale; righe: number }> = [
  { campo: 'whatsapp_breve', righe: 3 },
  { campo: 'whatsapp_lungo', righe: 6 },
  { campo: 'email_oggetto', righe: 1 },
  { campo: 'email_testo', righe: 8 },
];
/** Il codice d'esempio dell'anteprima: ha la forma di un codice vero, non è di nessuno. */
const CODICE_ESEMPIO = 'K7M2QX9P';

export const DialogoMateriale: React.FC<Props> = ({ aperto, materiale, ordineNuovo, onChiudi, onSalvato, onErrore }) => {
  const { t } = useTranslation('partner');
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [tipo, setTipo] = useState<TipoMateriale>('iscrizione');
  const [destinazione, setDestinazione] = useState('');
  const [settore, setSettore] = useState('');
  const [lingua, setLingua] = useState('it');
  const [ordine, setOrdine] = useState('');
  const [attivo, setAttivo] = useState(true);
  const [messaggi, setMessaggi] = useState<MessaggiDelMateriale>(MESSAGGI_VUOTI);
  const [lavora, setLavora] = useState(false);

  useEffect(() => {
    if (!aperto) return;
    setNome(materiale?.nome ?? '');
    setDescrizione(materiale?.descrizione ?? '');
    setTipo(materiale?.tipo ?? 'iscrizione');
    setDestinazione(materiale?.destinazione ?? '');
    setSettore(materiale?.settore ?? '');
    setLingua(materiale?.lingua ?? 'it');
    setOrdine(String(materiale?.ordine ?? ordineNuovo));
    setAttivo(materiale?.attivo ?? true);
    setMessaggi(materiale?.messaggi ?? MESSAGGI_VUOTI);
  }, [aperto, materiale, ordineNuovo]);

  const destinazioneVera = tipo === 'iscrizione' ? null : destinazione.trim() || null;
  const problemi = useMemo(
    () => problemiDelMateriale({ nome, descrizione, tipo, destinazione: destinazioneVera, messaggi }),
    [nome, descrizione, tipo, destinazioneVera, messaggi],
  );
  const anteprima = componiMessaggio(messaggi.whatsapp_breve, {
    link: indirizzoInvito(CODICE_ESEMPIO, destinazioneVera), nome: 'Mario', mittente: 'Renato Spiga',
  });
  const ordineNumero = Number.parseInt(ordine, 10);

  const salva = async () => {
    setLavora(true);
    try {
      await salvaMateriale({
        ...(materiale ? { materiale_id: materiale.id } : {}),
        nome: nome.trim(),
        descrizione: descrizione.trim(),
        tipo,
        destinazione: destinazioneVera,
        settore: settore.trim().toLowerCase() || null,
        messaggi,
        lingua,
        attivo,
        ordine: Number.isInteger(ordineNumero) ? ordineNumero : ordineNuovo,
      });
      onSalvato();
      onChiudi();
    } catch (e) {
      onErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavora(false);
    }
  };

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} fullWidth maxWidth="md">
      <DialogTitle>{materiale ? t('dialogo_materiale.titolo_modifica') : t('dialogo_materiale.titolo_nuovo')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('dialogo_materiale.nome')} helperText={t('dialogo_materiale.nome_aiuto')} value={nome} onChange={(e) => setNome(e.target.value)} size="small" fullWidth />
            <TextField select label={t('dialogo_materiale.tipo')} value={tipo} onChange={(e) => setTipo(e.target.value as TipoMateriale)} size="small" sx={{ minWidth: 220 }}>
              {TIPI_MATERIALE.map((x) => <MenuItem key={x} value={x}>{t(`tipo_materiale.${x}`)}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label={t('dialogo_materiale.descrizione')} helperText={t('dialogo_materiale.descrizione_aiuto')} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} size="small" fullWidth />
          {tipo !== 'iscrizione' && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label={t('dialogo_materiale.destinazione')}
                helperText={t('dialogo_materiale.destinazione_aiuto')}
                value={destinazione}
                onChange={(e) => setDestinazione(e.target.value)}
                size="small"
                fullWidth
              />
              <Button
                href={`${INDIRIZZO_SITO}${destinazioneVera ?? ''}`}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!destinazioneVera}
                sx={{ textTransform: 'none', flexShrink: 0, mt: 0.25 }}
              >
                {t('kit.apri')}
              </Button>
            </Stack>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField label={t('dialogo_materiale.settore')} helperText={t('dialogo_materiale.settore_aiuto')} value={settore} onChange={(e) => setSettore(e.target.value)} size="small" fullWidth />
            <TextField select label={t('dialogo_materiale.lingua')} value={lingua} onChange={(e) => setLingua(e.target.value)} size="small" sx={{ minWidth: 140 }}>
              <MenuItem value="it">{t('lingue.it')}</MenuItem>
              <MenuItem value="en">{t('lingue.en')}</MenuItem>
            </TextField>
            <TextField label={t('dialogo_materiale.ordine')} value={ordine} onChange={(e) => setOrdine(e.target.value)} size="small" inputProps={{ inputMode: 'numeric' }} sx={{ width: 110 }} />
          </Stack>
          <FormControlLabel control={<Checkbox checked={attivo} onChange={(e) => setAttivo(e.target.checked)} size="small" />} label={t('dialogo_materiale.attivo')} />

          <Box>
            <Typography sx={{ fontWeight: 700, color: tokens.color.graphite }}>{t('dialogo_materiale.messaggi')}</Typography>
            <Typography variant="body2" sx={{ color: tokens.color.ash }}>{t('dialogo_materiale.segnaposti')}</Typography>
          </Box>
          {CAMPI.map(({ campo, righe }) => (
            <TextField
              key={campo}
              label={t(`dialogo_materiale.${campo}`)}
              value={messaggi[campo]}
              onChange={(e) => setMessaggi((m) => ({ ...m, [campo]: e.target.value }))}
              size="small"
              fullWidth
              multiline={righe > 1}
              minRows={righe > 1 ? righe : undefined}
            />
          ))}
          {messaggi.whatsapp_breve.trim() && (
            <Box sx={{ p: 1.5, borderRadius: `${tokens.radius.md}px`, bgcolor: tokens.color.bone }}>
              <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block', mb: 0.5 }}>{t('dialogo_materiale.anteprima')}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{anteprima}</Typography>
            </Box>
          )}
          {problemi.length > 0 && (
            <Alert severity="info">
              {[...new Set(problemi.map((p) => t(`problemi_materiale.${p.codice}`, { campo: p.campo ? t(`dialogo_materiale.${p.campo}`) : '' })))].join(' ')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavora} sx={{ textTransform: 'none' }}>{t('azioni.annulla')}</Button>
        <Button variant="contained" onClick={() => void salva()} disabled={problemi.length > 0 || lavora} startIcon={lavora ? <CircularProgress size={14} /> : undefined} sx={{ textTransform: 'none' }}>
          {t('azioni.salva')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
